import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, SignOut, Trash, MusicNote, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// Hoisted framer-motion prop objects — module-level constants avoid creating
// new identities on every render.
const CARD_INITIAL = { opacity: 0, y: 16 };
const CARD_ANIMATE = { opacity: 1, y: 0 };
const CARD_EXIT = { opacity: 0 };
const CARD_HOVER = { y: -4 };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [bpm, setBpm] = useState(120);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      logger.error("Failed to load projects:", e?.message || e);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function createProject(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name: name.trim(), bpm: Number(bpm) || 120, description: "" });
      setOpen(false);
      setName("");
      setBpm(120);
      toast.success("Project created");
      nav(`/studio/${data.id}`);
    } catch (e) {
      logger.error("Create project failed:", e?.message || e);
      toast.error("Failed to create");
    } finally {
      setCreating(false);
    }
  }

  async function delProject(id) {
    if (!confirm("Delete this project? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch (e) {
      logger.error("Delete failed:", e?.message || e);
      toast.error("Failed to delete");
    }
  }

  return (
    <div className="min-h-screen bg-[#C7C9CC] text-[#0A0A0A]">
      <header className="sticky top-0 z-20 bg-[#C7C9CC]/95 backdrop-blur border-b border-[#B0B3B7]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tighter" data-testid="brand-logo">BEAT BOX</Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-sm text-[#2B2B2B] font-mono uppercase tracking-widest" data-testid="user-name">
              {user?.name || user?.email}
            </span>
            <Button
              variant="ghost" onClick={async () => { await logout(); nav("/"); }}
              data-testid="logout-btn"
              className="bb-pill border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] h-10 px-4 text-sm font-semibold"
            >
              <SignOut className="mr-2" size={14} weight="bold" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 lg:px-12 py-10 lg:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="bb-tag">PROJECT LIBRARY</span>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter leading-[0.92] mt-4">
              Your studio.
            </h1>
            <p className="text-[#2B2B2B] mt-3 text-base">
              {projects.length} project{projects.length === 1 ? "" : "s"} in your booth.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-project-btn"
                className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-14 px-6 text-base font-semibold"
              >
                <Plus className="mr-2" size={18} weight="bold" /> New project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-[#D0D2D5] rounded-3xl text-[#0A0A0A]">
              <DialogHeader>
                <DialogTitle className="font-display text-3xl tracking-tighter">New project</DialogTitle>
              </DialogHeader>
              <form onSubmit={createProject} className="space-y-4 mt-2">
                <div>
                  <Label className="text-[#6E7378] uppercase text-xs tracking-widest font-mono">Name</Label>
                  <Input
                    required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="My first banger"
                    className="mt-2 bg-white border-[#D0D2D5] rounded-full h-12 px-5"
                    data-testid="new-project-name-input"
                  />
                </div>
                <div>
                  <Label className="text-[#6E7378] uppercase text-xs tracking-widest font-mono">BPM</Label>
                  <Input
                    type="number" min="40" max="240" value={bpm}
                    onChange={(e) => setBpm(e.target.value)}
                    className="mt-2 bg-white border-[#D0D2D5] rounded-full h-12 px-5 font-mono"
                    data-testid="new-project-bpm-input"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="submit" disabled={creating}
                    data-testid="create-project-submit-btn"
                    className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-12 px-6 font-semibold"
                  >
                    {creating ? "Creating…" : "Create & open"} <ArrowRight className="ml-2" size={16} weight="bold" />
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {(() => {
          // Extract 3-way loading/empty/populated switch out of nested ternaries
          // (report flagged Dashboard.jsx line 149).
          if (loading) {
            return (
              <div className="bb-card p-16 text-center text-[#6E7378] font-mono" data-testid="projects-loading">
                Loading projects…
              </div>
            );
          }
          if (projects.length === 0) {
            return (
              <div className="bb-card p-16 text-center" data-testid="projects-empty">
                <div className="w-16 h-16 rounded-full bg-[#A8FF1F] mx-auto mb-6 flex items-center justify-center">
                  <MusicNote size={28} className="text-[#0A0A0A]" weight="bold" />
                </div>
                <h3 className="font-display text-3xl tracking-tighter mb-2">No tracks yet</h3>
                <p className="text-[#2B2B2B] mb-6">Hit “New project” to start your first beat.</p>
                <Button
                  onClick={() => setOpen(true)}
                  className="bb-pill bg-[#0A0A0A] text-white hover:bg-[#2B2B2B] h-12 px-6 font-semibold"
                >
                  <Plus className="mr-2" size={16} weight="bold" /> Create project
                </Button>
              </div>
            );
          }
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-list">
              <AnimatePresence>
                {projects.map((p) => (
                  <motion.div
                    key={p.id}
                    initial={CARD_INITIAL}
                    animate={CARD_ANIMATE}
                    exit={CARD_EXIT}
                    whileHover={CARD_HOVER}
                    className="bb-card p-7 cursor-pointer group flex flex-col gap-4 min-h-[220px]"
                    onClick={() => nav(`/studio/${p.id}`)}
                    data-testid={`project-row-${p.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <span className="bb-tag">{p.bpm} BPM</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); delProject(p.id); }}
                        className="text-[#6E7378] hover:text-[#E11D48] transition-colors"
                        data-testid={`delete-project-${p.id}`}
                        aria-label="Delete"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                    <div className="mt-auto">
                      <h3 className="font-display text-3xl tracking-tighter leading-[0.95]">{p.name}</h3>
                      <div className="flex items-center justify-between mt-3">
                        <div className="font-mono text-xs text-[#6E7378] uppercase tracking-widest">
                          {p.track_count} track{p.track_count === 1 ? "" : "s"} · {new Date(p.updated_at).toLocaleDateString()}
                        </div>
                        <div className="w-9 h-9 rounded-full bg-[#A8FF1F] flex items-center justify-center group-hover:translate-x-1 transition-transform">
                          <ArrowUpRight size={14} weight="bold" className="text-[#0A0A0A]" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          );
        })()}
      </main>
    </div>
  );
}
